package com.example.mystybag.appCore.views.pages

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.Image
import androidx.compose.ui.layout.ContentScale
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.ui.components.native.CaptionCapitalization
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.appCore.logic.pages.BagsViewModel
import com.example.mystybag.appCore.model.pages.BagListItem
import com.example.mystybag.appCore.model.pages.BagSegment
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.rememberUseNativeStyleWebLayout
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p28
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p40
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p80
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.views.isDarkTheme
import dev.icerock.moko.resources.compose.stringResource

// Align with allergen delete / logout confirmation dialogs
private val DeactivateBagDialogWidthDp = 348.dp
private val DeactivateBagDialogCornerRadiusDp = 40.dp
private val DeactivateBagDialogPaddingHorizontalDp = 32.dp
private val DeactivateBagDialogPaddingVerticalDp = 28.dp
private val DeactivateBagDialogContentSpacingDp = 24.dp
private val DeactivateBagDialogTextBlockSpacingDp = 12.dp
private val DeactivateBagDialogButtonsSpacingDp = 8.dp

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DeactivateBagConfirmDialog(
    onDismiss: () -> Unit,
    onConfirm: () -> Unit
) {
    BasicAlertDialog(
        onDismissRequest = onDismiss,
        modifier = Modifier.padding(p4),
        properties = DialogProperties(dismissOnBackPress = true, dismissOnClickOutside = true),
        content = {
            Column(
                modifier = Modifier
                    .widthIn(max = DeactivateBagDialogWidthDp)
                    .clip(RoundedCornerShape(DeactivateBagDialogCornerRadiusDp))
                    .background(AppTheme.current.colors.surface)
                    .padding(
                        start = DeactivateBagDialogPaddingHorizontalDp,
                        top = DeactivateBagDialogPaddingVerticalDp,
                        end = DeactivateBagDialogPaddingHorizontalDp,
                        bottom = DeactivateBagDialogPaddingVerticalDp
                    )
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(DeactivateBagDialogTextBlockSpacingDp)
                ) {
                    Texts.HeadlineMedium(text = stringResource(SR.strings.deactivate_bag_confirmation_title))
                    Texts.LabelLarge(
                        text = stringResource(SR.strings.deactivate_bag_confirmation_message),
                        color = AppTheme.current.colors.onSurfaceVariant
                    )
                }
                Spacer(modifier = Modifier.height(DeactivateBagDialogContentSpacingDp))
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(DeactivateBagDialogButtonsSpacingDp)
                ) {
                    DefaultButton(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(p56),
                        caption = stringResource(SR.strings.deactivate_bag_confirm),
                        type = DefaultButtonType.PRIMARY,
                        callback = onConfirm
                    )
                    DefaultButton(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(p56),
                        caption = stringResource(SR.strings.cancel),
                        type = DefaultButtonType.SECONDARY_NEUTRAL,
                        callback = onDismiss
                    )
                }
            }
        }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun BagsView(viewModel: BagsViewModel) {
    val uiState by viewModel.uiState.subscribeAsState()
    val colors = AppTheme.current.colors
    val useNativeStyleWebLayout = rememberUseNativeStyleWebLayout()

    UIView(
        modifier = Modifier
            .fillMaxSize()
            .background(colors.background),
        viewModel = viewModel,
        showTopBar = false,
        removeVerticalPadding = true,
        applyBottomInsetSpacer = false
    ) {
        if (uiState.bagIdPendingDeactivate != null) {
            DeactivateBagConfirmDialog(
                onDismiss = viewModel::onDismissDeactivateBagDialog,
                onConfirm = viewModel::onConfirmDeactivateBag
            )
        }
        Box(modifier = Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(start = p16, end = p16, top = p16)
            ) {
            if (useNativeStyleWebLayout) {
                Texts.DisplaySmall(
                    text = stringResource(SR.strings.bags),
                    modifier = Modifier.fillMaxWidth(),
                    color = colors.onSurface
                )
                Spacer(modifier = Modifier.height(p12))
            }
            // Tabs: show when we have any loaded bags so user can switch back from empty expired-segment list
            if (uiState.allBags.isNotEmpty() && !uiState.isLoading) {
                BagsTabRow(
                    selectedSegment = uiState.selectedSegment,
                    onSegmentSelected = viewModel::selectSegment
                )
                Spacer(modifier = Modifier.height(p12))
            }

            when {
                uiState.isLoading -> {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .weight(1f),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                }
                uiState.bags.isEmpty() && !uiState.isLoading -> {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .weight(1f),
                        contentAlignment = Alignment.Center
                    ) {
                        Texts.BodyMedium(text = sharedString(SR.strings.no_bags_found))
                    }
                }
                else -> {
                    LazyColumn(
                        modifier = Modifier
                            .fillMaxWidth()
                            .weight(1f)
                            .scrollEdgeFade(showTop = false),
                        verticalArrangement = Arrangement.spacedBy(p16)
                    ) {
                        items(uiState.bags, key = { it.bagId }) { bag ->
                            val isActiveSegment = uiState.selectedSegment == BagSegment.ACTIVE
                            BagItemCard(
                                bag = bag,
                                // Edit is only meaningful on active bags AND requires bag-edit permission
                                // (staff without canCreateBags can only view bags).
                                canEdit = isActiveSegment && uiState.canCreateBags,
                                showDeactivateButton = isActiveSegment && uiState.canCreateBags,
                                showDepletedActiveWarning = isActiveSegment && bag.availableCount == 0,
                                onCardClick = { viewModel.selectBag(bag.bagId) },
                                onEdit = { viewModel.navigateToEditBag(bag) },
                                onDeactivate = { viewModel.onDeactivateBagClick(bag.bagId) }
                            )
                        }
                    }
                }
            }

            if (uiState.locations.isNotEmpty() && uiState.canCreateBags && PlatformHelper.isWeb) {
                Spacer(modifier = Modifier.height(p16))
                DefaultButton(
                    modifier = Modifier
                        .fillMaxWidth(0.5f)
                        .align(Alignment.CenterHorizontally)
                        .height(p56)
                        .padding(bottom = 32.dp),
                    caption = stringResource(SR.strings.add_bag),
                    capitalization = CaptionCapitalization.None,
                    contentPadding = PaddingValues(horizontal = p16, vertical = 0.dp),
                    callback = {
                        val firstLocationId = uiState.locations.firstOrNull()?.id
                        if (firstLocationId != null) {
                            viewModel.navigateToAddBag(firstLocationId)
                        }
                    },
                )
            }
        }

        // FAB "Добави торба" — Figma: 80×80, Primary variant, plus On Primary, 24dp padding, drop shadow
        if (uiState.locations.isNotEmpty() && uiState.canCreateBags && !PlatformHelper.isWeb) {
            FloatingActionButton(
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(p20)
                    .size(p80),
                containerColor = colors.inversePrimary,
                contentColor = colors.onPrimary,
                shape = CircleShape,
                onClick = {
                    val firstLocationId = uiState.locations.firstOrNull()?.id
                    if (firstLocationId != null) {
                        viewModel.navigateToAddBag(firstLocationId)
                    }
                }
            ) {
                // Plus: 2.dp stroke, 28.dp height (vertical bar 2×28, horizontal bar 28×2)
                Box(
                    modifier = Modifier.size(28.dp, 28.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Box(
                        modifier = Modifier
                            .size(2.dp, 28.dp)
                            .background(colors.onPrimary)
                    )
                    Box(
                        modifier = Modifier
                            .size(28.dp, 2.dp)
                            .background(colors.onPrimary)
                    )
                }
            }
        }
    }
    }
}

/** Figma: card 380×229, cornerRadius 28, padding 20, itemSpacing 16. */
@Composable
private fun BagItemCard(
    bag: BagListItem,
    canEdit: Boolean,
    showDeactivateButton: Boolean,
    showDepletedActiveWarning: Boolean,
    onCardClick: () -> Unit,
    onEdit: () -> Unit,
    onDeactivate: () -> Unit
) {
    val colors = AppTheme.current.colors
    val custom = AppTheme.current.customColors
    val isDark by isDarkTheme.subscribeAsState()
    val cardShape = RoundedCornerShape(p28)
    val warningAccent = remember(custom.alertOrange, custom.vividYellow) {
        lerp(custom.alertOrange, custom.vividYellow, 0.42f)
    }
    val borderWidth = if (showDepletedActiveWarning) 2.dp else p1
    val borderColor = if (showDepletedActiveWarning) {
        warningAccent.copy(alpha = 0.95f)
    } else {
        colors.outline
    }
    val warningBannerBg = if (showDepletedActiveWarning) {
        if (isDark) {
            warningAccent.copy(alpha = 0.34f)
        } else {
            lerp(Color.White, warningAccent, 0.38f).copy(alpha = 0.96f)
        }
    } else {
        Color.Transparent
    }
    val warningBannerText = if (showDepletedActiveWarning) {
        if (isDark) Color(0xFFFFEFD6) else Color(0xFF6B3D00)
    } else {
        colors.onSurface
    }
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .border(borderWidth, borderColor, cardShape)
            .clickable { onCardClick() },
        shape = cardShape,
        colors = CardDefaults.cardColors(containerColor = colors.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(p20),
            verticalArrangement = Arrangement.spacedBy(p16)
        ) {
            if (showDepletedActiveWarning) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(p12))
                        .background(warningBannerBg)
                        .padding(horizontal = p12, vertical = p8)
                ) {
                    Texts.LabelLarge(
                        text = stringResource(SR.strings.merchant_bag_depleted_still_active),
                        color = warningBannerText
                    )
                }
            }
            // Header: title + edit (pencil) — Figma: primary circle, on-primary icon
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(p40),
                horizontalArrangement = Arrangement.spacedBy(p12),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Texts.TitleMedium(
                    text = bag.title?.takeIf { it.isNotBlank() } ?: bag.locationName,
                    color = colors.onSurface,
                    modifier = Modifier.weight(1f)
                )
                Row(
                    horizontalArrangement = Arrangement.spacedBy(p12),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (canEdit) {
                        Box(
                            modifier = Modifier
                                .size(p40)
                                .clip(CircleShape)
                                .background(colors.primary)
                                .clickable { onEdit() },
                            contentAlignment = Alignment.Center
                        ) {
                            Image(
                                painter = painterResource(SRC.images.staff_edit_icon),
                                contentDescription = stringResource(SR.strings.edit_short),
                                contentScale = ContentScale.Fit,
                                modifier = Modifier.size(p20),
                                colorFilter = ColorFilter.tint(colors.onPrimary)
                            )
                        }
                    }
                    if (showDeactivateButton) {
                        Box(
                            modifier = Modifier
                                .size(p40)
                                .clip(CircleShape)
                                .background(colors.error)
                                .noRippleClickable { onDeactivate() },
                            contentAlignment = Alignment.Center
                        ) {
                            Image(
                                painter = painterResource(SRC.images.mol_trash_icon),
                                contentDescription = stringResource(SR.strings.deactivate_bag_confirm),
                                contentScale = ContentScale.Fit,
                                modifier = Modifier.size(p20),
                                colorFilter = ColorFilter.tint(colors.onError)
                            )
                        }
                    }
                }
            }
            HorizontalDivider(
                modifier = Modifier.fillMaxWidth(),
                thickness = p1,
                color = colors.outlineVariant
            )
            // Details — itemSpacing 12, key-value rows with value right-aligned
            Column(verticalArrangement = Arrangement.spacedBy(p12)) {
                BagDetailRow(
                    label = stringResource(SR.strings.store_colon),
                    value = bag.locationName
                )
                BagDetailRow(
                    label = stringResource(SR.strings.active_from)+":",
                    value = bag.activeFromFormatted
                )
                BagDetailRow(
                    label = stringResource(SR.strings.active_to)+":",
                    value = bag.activeToFormatted
                )
                BagDetailRow(
                    label = stringResource(SR.strings.price_colon),
                    value = "${com.example.mystybag.core.expected.format("%.2f", bag.price)} ${bag.currency}"
                )
                BagDetailRow(
                    label = stringResource(SR.strings.available_bags_colon),
                    value = "${bag.availableCount} of ${bag.totalCount}"
                )
            }
        }
    }
}

@Composable
private fun BagDetailRow(label: String, value: String) {
    val colors = AppTheme.current.colors
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Texts.BodyMedium(
            text = label,
            color = colors.onSurfaceVariant,
            modifier = Modifier.weight(1f)
        )
        // Figma: Title Small SemiBold 14 (onSurface, right-aligned)
        Texts.TitleSmall(
            text = value,
            color = colors.onSurface,
            textAlign = TextAlign.End
        )
    }
}

private val SegmentControlHeight = 48.dp
private val SegmentControlPadding = 4.dp
private val SegmentPillRadius = 200.dp
private val SegmentHeight = 40.dp

@Composable
private fun BagsTabRow(
    selectedSegment: BagSegment,
    onSegmentSelected: (BagSegment) -> Unit
) {
    val colors = AppTheme.current.colors
    // Same sliding pill animation as WelcomeView segment control
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(SegmentControlHeight)
            .clip(RoundedCornerShape(SegmentPillRadius))
            .background(colors.surfaceContainerLowest)
            .padding(SegmentControlPadding)
    ) {
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxWidth()
                .height(SegmentHeight)
        ) {
            val segmentWidth = maxWidth / 2
            val tabIndex = if (selectedSegment == BagSegment.ACTIVE) 0f else 1f
            val fraction by animateFloatAsState(
                targetValue = tabIndex,
                animationSpec = tween(durationMillis = 250),
                label = "segmentIndicator"
            )
            val slidingOffsetDp = (segmentWidth.value * fraction).dp
            // Sliding pill (behind text)
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .offset(x = slidingOffsetDp)
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth(0.5f)
                        .fillMaxHeight()
                        .clip(RoundedCornerShape(SegmentPillRadius))
                        .background(colors.surface)
                )
            }
            // Labels on top (clickable)
            Row(
                modifier = Modifier.fillMaxSize(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                listOf(
                    BagSegment.ACTIVE to stringResource(SR.strings.tab_active),
                    BagSegment.ENDED to stringResource(SR.strings.tab_inactive)
                ).forEach { (segment, label) ->
                    val selected = segment == selectedSegment
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxWidth()
                            .height(SegmentHeight)
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null
                            ) { onSegmentSelected(segment) },
                        contentAlignment = Alignment.Center
                    ) {
                        Texts.TitleMedium(
                            text = label,
                            color = if (selected) colors.primary else colors.onSurfaceVariant
                        )
                    }
                }
            }
        }
    }
}
